import type { ReactNode } from 'react';
import './PickerCell.css';

interface PickerCellProps {
    label: string;
    color?: string | null;
    disabled?: boolean;
    onSelect: () => void;
    testId?: string;
}

// A single row in an offline role/department picker list, matching the
// Figma "PickerCell" — a colored lettered avatar plus a label, no chevron
// or radio indicator. No Sous Chef component matches this flat, one-tap
// row shape (ActionList always renders a chevron; RadioGroupField always
// renders a radio indicator; Avatar has no custom-background API — it only
// auto-picks from an internal palette and ignores a custom `color` prop
// except to tint its own fallback icon). Instead this reuses the codebase's
// existing convention for role-color swatches (a plain span + inline
// `backgroundColor`, as seen in `StartShiftRoleModal`'s `punch-pad__role-color`).
export function PickerCell({
    label,
    color,
    disabled,
    onSelect,
    testId
}: PickerCellProps) {
    return (
        <button
            type="button"
            className="picker-cell"
            disabled={disabled}
            onClick={onSelect}
            data-testid={testId}
        >
            <span
                className="picker-cell__avatar"
                style={color ? { backgroundColor: color } : undefined}
                aria-hidden="true"
            >
                {label.charAt(0).toUpperCase()}
            </span>
            <span className="picker-cell__label">{label}</span>
        </button>
    );
}

interface PickerListProps {
    title?: string;
    children: ReactNode;
    testId?: string;
}

// Groups PickerCells under an optional title, matching the Figma department
// section headers (e.g. "FRONT OF HOUSE").
export function PickerList({ title, children, testId }: PickerListProps) {
    return (
        <div className="picker-list" data-testid={testId}>
            {title && (
                <span
                    className="picker-list__title"
                    data-testid={testId ? `${testId}-title` : undefined}
                >
                    {title}
                </span>
            )}
            {children}
        </div>
    );
}
